import { Badge, Checkbox, Label } from '@yeshuman/sdk'
import { ArrowRight } from 'lucide-react'
import type { ReactNode } from 'react'

import type { ImproveChange } from '../../hooks/use-improve'

const ROLE_FIELD_LABELS: Record<string, string> = {
  title: 'Title',
  start_date: 'Start date',
  end_date: 'End date',
  description: 'Description',
}

function text(value: unknown): string {
  if (value === null || value === undefined || value === '') return ''
  return String(value)
}

function DiffText({ value, tone }: { value: unknown; tone: 'before' | 'after' }) {
  const body = text(value)
  if (!body) return <p className="text-xs italic text-muted-foreground">Empty</p>
  return (
    <p
      className={
        tone === 'before'
          ? 'whitespace-pre-wrap text-sm text-muted-foreground line-through'
          : 'whitespace-pre-wrap text-sm text-foreground'
      }
    >
      {body}
    </p>
  )
}

function BeforeAfter({ before, after }: { before: unknown; after: unknown }) {
  return (
    <div className="grid gap-2 rounded-md border border-border/60 bg-muted/40 px-3 py-2.5">
      <div className="space-y-1">
        <p className="text-xs font-medium text-muted-foreground">Before</p>
        <DiffText value={before} tone="before" />
      </div>
      <div className="space-y-1">
        <p className="text-xs font-medium text-muted-foreground">After</p>
        <DiffText value={after} tone="after" />
      </div>
    </div>
  )
}

function ChangeBody({ change }: { change: ImproveChange }) {
  switch (change.kind) {
    case 'field':
      return <BeforeAfter before={change.before} after={change.after} />
    case 'skill':
    case 'role_skill':
      return <Badge variant="successSoft">Add {text(change.after)}</Badge>
    case 'remove_skill':
      return <Badge variant="destructiveSoft">Remove {text(change.before)}</Badge>
    case 'skill_type':
      return (
        <span className="flex items-center gap-1.5 text-sm">
          <Badge variant="secondarySoft">{text(change.before)}</Badge>
          <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
          <Badge variant="secondarySoft">{text(change.after)}</Badge>
        </span>
      )
    case 'experience':
      return <BeforeAfter before={null} after={change.after} />
    case 'role_update': {
      const before = (change.before ?? {}) as Record<string, unknown>
      const after = (change.after ?? {}) as Record<string, unknown>
      return (
        <div className="space-y-2">
          {Object.keys(after).map((key) => (
            <div key={key} className="space-y-1">
              <span className="text-xs font-medium">{ROLE_FIELD_LABELS[key] ?? key}</span>
              <BeforeAfter before={before[key]} after={after[key]} />
            </div>
          ))}
        </div>
      )
    }
    case 'new_role': {
      const role = (change.after ?? {}) as Record<string, unknown>
      const skills = Array.isArray(role.skills) ? (role.skills as string[]) : []
      const dates = [text(role.start_date), text(role.end_date) || 'present'].filter(Boolean).join(' – ')
      return (
        <div className="space-y-2">
          <BeforeAfter before={null} after={[dates, text(role.description)].filter(Boolean).join('\n')} />
          {skills.length ? (
            <div className="flex flex-wrap gap-1.5">
              {skills.map((name) => (
                <Badge key={name} variant="secondarySoft">
                  {name}
                </Badge>
              ))}
            </div>
          ) : null}
        </div>
      )
    }
    default:
      return null
  }
}

export function ImproveChangeRow({
  change,
  checked,
  disabled,
  onCheckedChange,
}: {
  change: ImproveChange
  checked: boolean
  disabled?: boolean
  onCheckedChange: (checked: boolean) => void
}) {
  const inputId = `improve-change-${change.id}`
  const title: ReactNode = change.kind === 'skill_type' ? `${change.label}: requirement` : change.label
  return (
    <li className="flex items-start gap-3 py-3">
      <Checkbox
        id={inputId}
        className="mt-0.5"
        checked={checked}
        disabled={disabled}
        onCheckedChange={(value) => onCheckedChange(value === true)}
      />
      <div className="min-w-0 flex-1 space-y-2">
        <Label htmlFor={inputId} className="leading-snug">
          {title}
        </Label>
        <ChangeBody change={change} />
      </div>
    </li>
  )
}
